/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { utils } from 'pixi.js-legacy';
import * as OpenType from 'opentype.js';
import { logger } from '@touchplan/logging';
import { Observable, from } from 'rxjs';
import { concatMap, take, toArray } from 'rxjs/operators';
import { withTimeoutRetryStrategy } from '../../services/rxjs-internal-extensions';

/**
 * The events that the FontManager emits.
 */
export enum EVENTS_FONT_MANAGER {
	'LOADED' = 'loaded',
	'ERROR' = 'error',
}

// font load retry strategy options
const LOAD_RETRY_STRATEGY_TIMEOUT = 1000;
const LOAD_RETRY_STRATEGY_INTENTS = 2;

/**
 * Singleton which exposes methods to manage loaded fonts. Dispatches events once a font has been loaded.
 */
export class FontManager extends utils.EventEmitter {
	constructor() {
		super();
	}
	/**
	 * Stores the parsed font data for all loaded fonts.
	 */
	private _cache: Map<string, any> = new Map();
	/** a cache of file path to font family, which the main cache is indexed by */
	private _fileCache: Map<string, string> = new Map();
	/**
	 * Returns the parsed font data for the provided fontFamily
	 */
	public getFont(fontFamily: string) {
		return this._cache.get(fontFamily);
	}
	/**
	 * Load and parse the give font.
	 * @param path the path of the font that will be loaded and parsed
	 */
	public loadFont(path: string): void {
		// a touch silly, but not sure if onLoad is actually used externally
		// It load the font as an observable with retry strategy
		this.loadFont$(path)
			.pipe(withTimeoutRetryStrategy(LOAD_RETRY_STRATEGY_TIMEOUT, LOAD_RETRY_STRATEGY_INTENTS))
			.subscribe({
				error: (e) => {
					logger.error('Error loading font', path, e);
				},
			});
	}

	/**
	 * Load and parse in order one by one the given fonts.
	 * @param paths an array with the paths of the fonts that will be loaded and parsed
	 */
	public loadFonts(paths: string[]): void {
		// load fonts in order as observable with retry strategy
		from(paths)
			.pipe(
				// we need to run observables in order to prevent calling the API at the same time avoiding errors
				// on number of concurrent connections
				concatMap((path) =>
					this.loadFont$(path).pipe(
						withTimeoutRetryStrategy(LOAD_RETRY_STRATEGY_TIMEOUT, LOAD_RETRY_STRATEGY_INTENTS)
					)
				),
				// ToArray is added to wait for all observables to return something and then complete observable after first emition
				// to avoid memory leaks
				toArray(),
				take(1)
			)
			.subscribe({
				error: (e) => {
					logger.error('Error loading fonts:', paths, e);
				},
			});
	}

	private loadFont$(path: string): Observable<[string, any]> {
		return new Observable((observer) => {
			OpenType.load(path, (err, font: any) => {
				// cache is set inside the observable as a side effect so if the observable hits the timeout
				// it can still set the font into the cache if it returns sometime after the timeout occurs
				this.onLoad(err, font);
				if (!err) {
					const fontFamily = font['names']['fontFamily']['en'];
					this._fileCache.set(path, fontFamily);
					observer.next([path, font]);
					observer.complete();
				} else {
					observer.error(err);
				}
			});
		});
	}
	/**
	 * Returns a parsed font file from OpenType.js that can be used for drawing
	 */
	public onLoad = (err, font: any): any => {
		if (err) {
			return this.onError;
		}
		const fontFamily = font['names']['fontFamily']['en'];
		// since we could potencional have duplicated calls because the retry strategy we must prevent emit an event twice
		if (!this._cache.get(fontFamily)) {
			logger.log('[FontManager] Load', fontFamily);
			this._cache.set(fontFamily, font);
			this.emit(EVENTS_FONT_MANAGER.LOADED, fontFamily, font);
		}
	};
	/**
	 * Called when an error occurred, and the font path could not be loaded or parsed.
	 */
	public onError = (err): void => {
		logger.log('[FontManager]', err);
		this.emit(EVENTS_FONT_MANAGER.ERROR, err);
	};
	/**
	 * Called when the FontManager should be destroyed
	 */
	public destroy(): void {
		this.removeAllListeners();
		this._cache.clear();
		this._cache = null;
	}

	/**
	 * Remove all references. Different from FontManager.destroy in that it does not null out the instance, only cleans up memory
	 */
	public purge(): void {
		this._cache.clear();
		this.removeAllListeners();
	}
}

export const fontManager = new FontManager();

// TODO: Remove. Useful for testing.
// window.fontManager = fontManager;
